import { throttleErrorMessage } from '@hooks/use-throttled-mutation';

type AxiosLikeError = {
  response?: {
    data?: unknown;
  };
};

const isAxiosLikeError = (error: unknown): error is AxiosLikeError => {
  return typeof error === 'object' && error !== null && 'response' in error;
};

const MAX_MESSAGE_LENGTH = 200;

// A 500 answers with a whole HTML error page rather than a message, and that
// must never end up in the cell.
const isPresentableMessage = (value: string): boolean => {
  return value.length <= MAX_MESSAGE_LENGTH && !value.includes('<');
};

const findFirstStringLeaf = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    return isPresentableMessage(value) ? value : undefined;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findFirstStringLeaf(item);
      if (found !== undefined) {
        return found;
      }
    }
    return undefined;
  }

  if (typeof value === 'object' && value !== null) {
    for (const item of Object.values(value)) {
      const found = findFirstStringLeaf(item);
      if (found !== undefined) {
        return found;
      }
    }
    return undefined;
  }

  return undefined;
};

const SLUG_PATTERN = /^[a-z0-9]+(_[a-z0-9]+)+$/;

// The API answers with slugs ("invalid_email"), which are translated when we
// know them. The ones we don't know are internal names, not something to put
// in front of a user - anything else is a real sentence and worth showing.
export const isServerSlug = (message: string): boolean => {
  return SLUG_PATTERN.test(message);
};

export const getInlineEditErrorMessage = (error: unknown): string | undefined => {
  if (error === throttleErrorMessage) {
    return 'saveThrottled';
  }

  if (!isAxiosLikeError(error) || error.response?.data === undefined) {
    return undefined;
  }

  return findFirstStringLeaf(error.response.data);
};
